import { useIsFetching } from "@tanstack/react-query";
import type { Query, QueryObserverOptions } from "@tanstack/query-core";

const KM_DROPDOWN_LIST_PATH = /\/km\/dropdown-list/i;
const KM_API_PATH = /\/km\//i;
const DROPDOWN_LIST_PATH = /dropdown-list/i;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);

/**
 * Async select / KM dropdown queries should keep loading inside the control,
 * not trigger the app-wide Loading overlay.
 */
export const shouldSuppressGlobalLoading = (query: Query): boolean => {
  if (query.meta?.suppressGlobalLoading === true) {
    return true;
  }

  const { queryKey } = query;
  // Observer options (staleTime, refetchOnWindowFocus) are merged into query.options at runtime.
  const options = query.options as QueryObserverOptions;
  if (!Array.isArray(queryKey)) {
    return false;
  }

  if (
    queryKey.includes("kmDropdownPage") ||
    queryKey.includes("kmConceptsByIds")
  ) {
    return true;
  }

  if (
    queryKey.some(
      (part) =>
        typeof part === "string" &&
        (KM_DROPDOWN_LIST_PATH.test(part) ||
          KM_API_PATH.test(part) ||
          DROPDOWN_LIST_PATH.test(part)),
    )
  ) {
    return true;
  }

  // DynamicForm useCustomSelect: [fieldKey, dependencyVars, dependencyBodyVars]
  if (queryKey.length >= 3) {
    const lastTwo = queryKey.slice(-2);
    if (
      lastTwo.every(isPlainObject) &&
      options.staleTime === Infinity &&
      options.retry === false &&
      options.refetchOnWindowFocus === false
    ) {
      return true;
    }
  }

  return false;
};

export const useAppIsFetching = (): number =>
  useIsFetching({
    predicate: (query) => !shouldSuppressGlobalLoading(query),
  });
